import React, { useCallback } from 'react';
import { Pie } from 'react-chartjs-2';
import { useCommentBanksStats } from '../state/hooks';
import { useClassInsights } from './ClassInsightsContext';
import { statsBody } from './useFilters';
import { catalogClasses } from './catalogSelectors';
import { PanelHeader } from './PanelHeader';
import { PanelIconButton } from './PanelIconButton';
import { ExportFormatDropdown } from './ExportFormatDropdownNew';
import { ExpandIcon, ChevronDown } from './Icons';
import { SECTIONS } from './export/fileNaming';
import { CommentBanksModal } from './CommentBanksModal';
import DropdownMenu from '../DropdownMenu';
import SmallLoader from '../Loader/SmallLoader';
import DownLoad from '../../static/img/Download.svg';
import commentBankIcon from '../../static/img/commentUnSelected.svg';

export function CommentBanksPanel({
  mode = 'small',
  onExpand,
  onCollapse,
  open = true,
  onExited,
  exportMode = false,
}) {
  const { filters, debouncedFilters, setFilters, catalog } = useClassInsights();
  const chartRef = React.useRef(null);
  const rowRefs = React.useRef({});
  const [expandedSuggestion, setExpandedSuggestion] = React.useState(null);

  // JED-1066: empty classIds means "all classes", not "no classes". Gate the
  // query on whether the catalog has any class at all -- mirroring
  // KpiSummaryCards -- so selecting "All Classes" still loads this report
  // instead of leaving the panel blank while the KPIs show data.
  const hasClassScope = catalogClasses(catalog).length > 0;
  const { data, isFetching: isTileLoading } = useCommentBanksStats(
    tileBody(debouncedFilters, debouncedFilters.commentBankIds),
    hasClassScope
  );

  const commentBanksStats = data?.stats || [];
  const commentBanksList = (data?.options || []).map((bank) => ({
    id: bank.id,
    title: bank.title || bank.name || 'Untitled Bank',
    label: bank.title || bank.name || bank.id,
  }));

  const getSelectedIds = () => {
    if (commentBanksList.length === 0) return [];

    if (filters.commentBankIds && filters.commentBankIds.length > 0) {
      const validIds = filters.commentBankIds.filter((id) =>
        commentBanksList.some((item) => String(item.id) === String(id))
      );
      if (validIds.length > 0) return validIds;
    }

    return [String(commentBanksList[0].id)];
  };
  const selectedCommentBankIds = getSelectedIds();

  // Clean up stale URL IDs when scope changes. Skip when URL is empty (don't seed).
  const currentFilterIds = (filters.commentBankIds || []).join(',');
  const validFilterIds = selectedCommentBankIds.join(',');
  if (commentBanksList.length > 0 && currentFilterIds && currentFilterIds !== validFilterIds) {
    setFilters({ commentBankIds: selectedCommentBankIds });
  }

  const onCommentBankChange = useCallback(
    (ids) => {
      setFilters({ commentBankIds: ids || [] });
    },
    [setFilters]
  );
  const dropdownUiProps = {
    triggerFontSize: '16px',
    triggerPadding: '10px 16px',
  };

  const selectedIds = (selectedCommentBankIds || []).map((id) => String(id)).filter(Boolean);
  const selectedSet = new Set(selectedIds);
  const scopedStats = exportMode
    ? commentBanksStats
    : (commentBanksStats || []).filter((bank) => {
        const bankId = String(bank?.id ?? bank?.markingToolId ?? bank?._id ?? '');
        return selectedSet.has(bankId);
      });


  const suggestionMap = new Map();
  scopedStats.forEach((bank) => {
    (bank?.suggestions || []).forEach((suggestion) => {
      const key = String(suggestion.title || '').trim();
      if (!key) return;
      const usage = Number(suggestion.usagePercentage) || 0;
      const comments = (suggestion?.comments || [])
        .map((comment) => ({
          text: String(comment?.comment || '').trim(),
          percentage: Number(comment?.usagePercentage || 0),
        }))
        .filter((comment) => comment.text);

      const existing = suggestionMap.get(key);
      if (existing) {
        existing.percentage += usage;
        const existingCommentTexts = new Set(existing.comments.map(c => c.text));
        comments.forEach(comment => {
          if (!existingCommentTexts.has(comment.text)) {
            existing.comments.push(comment);
          }
        });
      } else {
        suggestionMap.set(key, {
          name: key,
          percentage: usage,
          comments,
        });
      }
    });
  });


  const items = Array.from(suggestionMap.values())
    .map((suggestion) => ({
      name: suggestion.name,
      percentage: Number(suggestion.percentage.toFixed(1)),
      comments: suggestion.comments,
    }))
    .filter((item) => Number.isFinite(item.percentage) && item.percentage > 0)
    .sort((a, b) => b.percentage - a.percentage);


  const itemsWithColors = items.map((item, index) => ({
    ...item,
    color: generateColor(index),
  }));

  const hasPieData = itemsWithColors.length > 0;
  const chart = {
    labels: hasPieData ? itemsWithColors.map((item) => item.name) : ['No data'],
    datasets: [
      {
        backgroundColor: hasPieData ? itemsWithColors.map((item) => item.color) : ['#E5E0EB'],
        data: hasPieData ? itemsWithColors.map((item) => item.percentage) : [100],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    onClick: (_event, elements) => {
      if (!elements?.length) return;
      const clickedIndex = elements[0].index;
      const clickedSuggestionName = itemsWithColors[clickedIndex]?.name;
      if (clickedSuggestionName) {
        setExpandedSuggestion((prev) => (prev === clickedSuggestionName ? null : clickedSuggestionName));
        requestAnimationFrame(() => {
          rowRefs.current[clickedSuggestionName]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    },
    plugins: {
      tooltip: {
        enabled: true,
        titleFont: {
          family: 'IBM Plex Sans',
        },
        bodyFont: {
          family: 'IBM Plex Sans',
        },
        callbacks: {
          title: (items) => items?.[0]?.label ?? '',
          label: () => null,
        },
      },
      legend: {
        display: false,
        labels: {
          font: {
            family: 'IBM Plex Sans',
          },
        },
      },
    },
    maintainAspectRatio: false,
  };

  const commentBankCsv = [];
  scopedStats.forEach((bank, bankIndex) => {
    const bankTitle = String(bank?.title || bank?.name || `Comment Bank ${bankIndex + 1}`).trim();
    if (scopedStats.length > 1) {
      commentBankCsv.push({
        'Feedback Area': `Comment Bank: ${bankTitle}`,
        'Feedback Area %': '',
        Suggestion: '',
        'Suggestion %': '',
      });
    }

    (bank?.suggestions || []).forEach((suggestion) => {
      const suggestionTitle = String(suggestion?.title || '').trim();
      if (!suggestionTitle) return;

      const suggestionComments = Array.isArray(suggestion?.comments) ? suggestion.comments : [];
      if (suggestionComments.length === 0) {
        commentBankCsv.push({
          'Feedback Area': suggestionTitle,
          'Feedback Area %': `${Number(suggestion?.usagePercentage || 0).toFixed(1)}%`,
          Suggestion: '-',
          'Suggestion %': '',
        });
      }

      suggestionComments.forEach((comment, commentIndex) => {
        const commentText = String(comment?.comment || '').trim();
        if (!commentText) return;

        commentBankCsv.push({
          'Feedback Area': commentIndex === 0 ? suggestionTitle : '',
          'Feedback Area %': commentIndex === 0 ? `${Number(suggestion?.usagePercentage || 0).toFixed(1)}%` : '',
          Suggestion: commentText,
          'Suggestion %': `${Number(comment?.usagePercentage || 0).toFixed(1)}%`,
        });
      });
    });
  });

  const renderedItems = itemsWithColors.map((item, index) => {
    const isExpanded = expandedSuggestion === item.name;
    const hasComments = (item.comments || []).length > 0;

    return (
      <div
        key={index}
        ref={(el) => {
          rowRefs.current[item.name] = el;
        }}
        className="ci-comment-row-wrapper"
      >
        <div className="ci-comment-row">
          <div className="ci-comment-left">
            <span className="ci-comment-dot" style={{ backgroundColor: item.color, color: item.color }} />
            <span className="ci-comment-name">{item.name}</span>
          </div>
          <div className="ci-comment-right">
            <span className="ci-comment-value">{item.percentage}%</span>
            {hasComments && (
              <button
                type="button"
                className={`ci-comment-chevron-btn ${isExpanded ? 'open' : ''}`}
                onClick={() => {
                  setExpandedSuggestion((prev) => (prev === item.name ? null : item.name));
                  requestAnimationFrame(() => {
                    rowRefs.current[item.name]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  });
                }}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.name}`}
              >
                <ChevronDown />
              </button>
            )}
            {!hasComments && <ChevronDown />}
          </div>
        </div>
        {hasComments && isExpanded && (
          <div className="ci-comment-children">
            {item.comments.map((comment, commentIndex) => (
              <div key={commentIndex} className="ci-comment-child-row">
                <span className="ci-comment-child-name">{comment.text}</span>
                <span className="ci-comment-child-percentage">{comment.percentage.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  });

  if (mode === 'expanded') {
    return (
      <CommentBanksModal
        open={open}
        onClose={onCollapse}
        onExited={onExited}
        commentBanksList={commentBanksList}
        selectedCommentBankIds={selectedCommentBankIds}
        onCommentBankChange={onCommentBankChange}
        commentBanksStats={commentBanksStats}
        isLoading={isTileLoading}
        exportMode={exportMode}
      />
    );
  }

  return (
    <section className="ci-panel ci-comment-bank-panel" data-section="comment-banks">
      <PanelHeader title="Specific Comments">
        <DropdownMenu
          multiselect
          singleSelect
          options={commentBanksList}
          selectedValues={selectedIds.length > 0 ? [selectedIds[0]] : []}
          onChange={(values) => {
            if (!values || values.length === 0 || values[0] == null) {
              return;
            }
            onCommentBankChange([String(values[0])]);
          }}
          placeholder="Select specific comment"
          getOptionLabel={(opt) => opt.title || opt.name}
          getOptionValue={(opt) => String(opt.id)}
          alwaysWhite
          showAllOption={false}
          showCheckboxes
          showSearch
          leadingIcon={commentBankIcon}
          compactDisplay
          triggerWidth="clamp(120px, 40vw, 350px)"
          {...dropdownUiProps}
          dropdownHeader="Specific comments"
          dropdownSubtitle="Select specific comments to show data for"
        />
        <ExportFormatDropdown
          section={SECTIONS.COMMENT_BANKS}
          data={commentBanksStats}
          csvData={commentBankCsv}
          chartRef={chartRef}
          trigger={
            <PanelIconButton aria-label="Download">
              <img src={DownLoad} className="ci-download-icon" alt="" />
            </PanelIconButton>
          }
        />
        <PanelIconButton
          type="button"
          onClick={onExpand}
          aria-label="Expand"
          className="ci-icon-button-expand"
        >
          <ExpandIcon />
        </PanelIconButton>
      </PanelHeader>
      <div className="ci-comment-panel-content">
        {isTileLoading ? (
          <div className="ci-panel-loading">
            <SmallLoader />
          </div>
        ) : (
          <>
            <div className="ci-comment-list">
              {items.length === 0 ? (
                <div className="ci-empty-state">No data available</div>
              ) : (
                renderedItems
              )}
            </div>
            <div className="ci-comment-chart">
              <Pie
                key={`comment-banks-panel-pie-${items.length}-${chart.datasets[0].data.join('-')}`}
                ref={chartRef}
                data={chart}
                options={chartOptions}
                height={280}
              />
            </div>
          </>
        )}
      </div>

    </section>
  );
}

const generateColor = (index) => {
  const colors = [
    '#1ade54', '#9f6bfa', '#fa6be7', '#6bfaee', '#faee6b',
    '#406bda', '#f1955c', '#ff6b9d', '#00bcd4', '#8bc34a'
  ];
  return colors[index % colors.length];
};

const tileBody = (filters, ids) => ({
  ...statsBody(filters),
  markingToolIds:
    Array.isArray(ids) ? ids : null,
});
